import { useEffect, useState } from "react";
import { audio } from "../lib/audio";

/**
 * Phone dial keypad used during the vishing call: the attacker asks for the 2FA
 * code, the learner's "authenticator app" shows `targetCode`, and the learner
 * types it in. Submitting fires onSubmit (the disclosure). Teaching point: the
 * code is real, and reading/typing it to a caller hands the attacker the login.
 */
export function Keypad({
  targetCode,
  prompt,
  onSubmit,
  onReadyChange,
}: {
  targetCode: string;
  prompt?: string;
  onSubmit: (entered: string) => void;
  onReadyChange?: (ready: boolean) => void;
}) {
  const [entered, setEntered] = useState("");
  const len = targetCode.length;
  const ready = entered.length === len;
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];
  useEffect(() => {
    onReadyChange?.(ready);
  }, [ready, onReadyChange]);

  const press = (d: string) => {
    audio.dtmf(d);
    setEntered((e) => (e.length >= len ? e : e + d));
  };
  const back = () => setEntered((e) => e.slice(0, -1));

  return (
    <div className="keypad">
      <div className="keypad-prompt">
        <span className="keypad-prompt-label">{prompt ?? "Your authenticator app shows:"}</span>
        <strong className="keypad-code">{targetCode}</strong>
      </div>
      <div className={`keypad-display${ready ? "" : " is-waiting"}`} aria-label="Entered code">
        {Array.from({ length: len }).map((_, i) => (
          <span key={i} className={`keypad-cell ${entered[i] ? "on" : ""}`}>
            {entered[i] ?? ""}
          </span>
        ))}
      </div>
      <div className="keypad-grid">
        {keys.map((k) => (
          <button
            key={k}
            type="button"
            className="keypad-key"
            onClick={() => press(k)}
            disabled={entered.length >= len}
          >
            {k}
          </button>
        ))}
        <button type="button" className="keypad-key back" onClick={back} aria-label="Backspace">
          {"\u232B"}
        </button>
        <button
          type="button"
          className={`keypad-key send${ready ? " is-ready cf-blink-strong" : ""}`}
          onClick={() => ready && onSubmit(entered)}
          disabled={!ready}
          aria-label={ready ? "Confirm code" : undefined}
        >
          {"\u2713"}
        </button>
        {ready && (
          <button type="button" className="keypad-clickhere cf-blink-strong" aria-label="Send code" onClick={() => onSubmit(entered)}>
            Send code
          </button>
        )}
      </div>
      <div className="keypad-send-hint">{ready ? "Click Send code." : "Enter the code, then click Send code."}</div>
    </div>
  );
}
